import { useEffect, useState } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { usePostHog } from 'posthog-js/react';
import { initializeApiAuth } from '@/lib/api-client';
import { setUser as setSentryUser } from '@/lib/sentry';
import { ApiAuthContext } from '@/hooks/use-api-auth';
import { useStuckAuthRecovery } from '@/hooks/use-stuck-auth-recovery';

/**
 * After long inactivity, WorkOS AuthKit's silent token refresh can fail (expired
 * refresh token) without its promise ever settling, hanging getAccessToken()
 * indefinitely instead of rejecting. Race each attempt against this timeout so a
 * hung SDK call surfaces as a failure — routing into the existing retry/AuthError
 * path — instead of blocking every API call forever.
 */
const GET_ACCESS_TOKEN_TIMEOUT_MS = 5000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('getAccessToken timed out')), timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * ApiAuthProvider - Initializes API client with WorkOS token getter
 *
 * This component runs once on mount to configure the API client with
 * the WorkOS getAccessToken function. After initialization, all API
 * requests automatically fetch fresh tokens.
 *
 * Place this component inside AuthKitProvider in your app root.
 */
export function ApiAuthProvider({ children }: { children: React.ReactNode }) {
  const { getAccessToken, user, isLoading } = useAuth();
  const [isAuthReady, setIsAuthReady] = useState(false);
  const posthog = usePostHog();

  useStuckAuthRecovery(isLoading);

  useEffect(() => {
    initializeApiAuth(async () => {
      // WorkOS writes the token asynchronously after the auth redirect. Retry
      // briefly to bridge the gap between React state updating and the token
      // being available, avoiding a silent LoginRequiredError on first load.
      for (let attempt = 0; attempt < 5; attempt++) {
        try {
          const token = await withTimeout(getAccessToken(), GET_ACCESS_TOKEN_TIMEOUT_MS);
          if (token) return token;
        } catch {
          if (attempt < 4) await new Promise((r) => setTimeout(r, 150 * (attempt + 1)));
        }
      }
      return null;
    });
    setIsAuthReady(true);
  }, [getAccessToken]);

  useEffect(() => {
    if (user) {
      posthog.identify(user.id, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      });
      setSentryUser({ id: user.id, email: user.email });
    } else {
      posthog.reset();
      setSentryUser(null);
    }
  }, [user, posthog]);

  return <ApiAuthContext.Provider value={{ isAuthReady }}>{children}</ApiAuthContext.Provider>;
}
